import { ConfigFilterItem, ConfigItem } from 'Core/interfaces'

export enum AccrualAdjustmentStatuses {
	InProgress = 'InProgress',
	Approved = 'Approved',
	Declined = 'Declined'
}

export enum AccrualAdjustmentSearchFields {
	searchByServiceOccurrenceId = 'searchByServiceOccurrenceId'
}

export enum ServiceSiteSearchFields {
	searchById = 'searchById',
	searchByName = 'searchByName'
}

export enum AccrualAdjustmentFilterFields {
	BillingAgencyId = 'billingAgencyId',
	AdjustmentType = 'adjustmentType',
	Status = 'status',
	JournalDateMonth = 'journalDateMonth'
}

export enum AccrualAdjustmentFields {
	Id = 'id',
	JournalDate = 'journalDate',
	ServiceOccurrenceId = 'serviceOccurrenceId',
	ServiceSite = 'serviceSite',
	AccountGlRevenue = 'accountGlRevenue',
	AccountGlExpense = 'accountGlExpense',
	DepartmentId = 'departmentId',
	Region = 'region',
	Status = 'status',
	Revenue = 'revenue',
	Expense = 'expense',
	CurrencyCode = 'currencyCode',
	AdjustmentType = 'adjustmentType',
	BillingAgency = 'billingAgency',
	Notes = 'notes',
	AccrualId = 'accrualId'
}

export enum ReclassesAdjustmentFields {
	currentServiceSite = 'currentServiceSite',
	currentBillingAgency = 'currentBillingAgency',
	newServiceSite = 'newServiceSite',
	newBillingAgency = 'newBillingAgency',
	currentAccountGLRevenue = 'currentAccountGLRevenue',
	newAccountGLRevenue = 'newAccountGLRevenue',
	currentAccountGLExpense = 'currentAccountGLExpense',
	newAccountGLExpense = 'newAccountGLExpense',
	currentDepartmentId = 'currentDepartmentId',
	newDepartmentId = 'newDepartmentId',
	currentRegion = 'currentRegion',
	newRegion = 'newRegion'
}

export enum ManualAdjustmentRequestType {
	byServiceOccurrence = 'byServiceOccurrence',
	byServiceSite = 'byServiceSite',
	reclasses = 'reclasses'
}

export declare namespace AccrualAdjustment {
	interface DropdownOptionData {
		expenseGlAccountNumbers: string[]
		revenueGlAccountNumbers: string[]
		prodCodes: string[]
		departmentIds: string[]
	}

	interface PrepopulationData {
		revenueGlAccountNumbers: BasicString[]
		expenseGlAccountNumbers: BasicString[]
		departmentId: BasicString
		prodCode: BasicString
		billingAgencyId: BasicNumber
		billingAgencyName: BasicString
		serviceSiteId: BasicNumber
		serviceSiteName: BasicString
		currencyCode: BasicString
	}

	interface JournalDate {
		journalDate: BasicString
		isClosed: boolean
	}

	interface Basic {
		[AccrualAdjustmentFields.Id]: number
		[AccrualAdjustmentFields.JournalDate]: BasicString
		[AccrualAdjustmentFields.ServiceOccurrenceId]: BasicNumber
		[AccrualAdjustmentFields.ServiceSite]: Entity | null
		[AccrualAdjustmentFields.AccountGlRevenue]: BasicString
		[AccrualAdjustmentFields.AccountGlExpense]: BasicString
		[AccrualAdjustmentFields.DepartmentId]: BasicString
		[AccrualAdjustmentFields.Region]: BasicString
		[AccrualAdjustmentFields.Status]: Entity | null
		[AccrualAdjustmentFields.Revenue]: BasicNumber
		[AccrualAdjustmentFields.Expense]: BasicNumber
		[AccrualAdjustmentFields.CurrencyCode]: BasicString
		[AccrualAdjustmentFields.AdjustmentType]: Entity | null
		[AccrualAdjustmentFields.BillingAgency]: BasicString
		[AccrualAdjustmentFields.Notes]: BasicString
		[AccrualAdjustmentFields.AccrualId]: BasicNumber
	}

	namespace Add {
		interface Basic {
			[AccrualAdjustmentFields.Revenue]: BasicString
			[AccrualAdjustmentFields.Expense]: BasicString
			[AccrualAdjustmentFields.Notes]: BasicString
			[AccrualAdjustmentFields.CurrencyCode]: BasicNumber
		}

		interface Valid extends Basic {
			[AccrualAdjustmentFields.Notes]: string
			[AccrualAdjustmentFields.CurrencyCode]: number
		}

		namespace ServiceOccurrence {
			interface Basic extends Add.Basic {
				[AccrualAdjustmentFields.ServiceOccurrenceId]: BasicNumber
				[AccrualAdjustmentFields.ServiceSite]: BasicString
				[AccrualAdjustmentFields.BillingAgency]: BasicString
				[AccrualAdjustmentFields.AccountGlRevenue]: BasicString
				[AccrualAdjustmentFields.AccountGlExpense]: BasicString
			}

			interface Valid extends Basic {
				[AccrualAdjustmentFields.ServiceOccurrenceId]: number
				[AccrualAdjustmentFields.ServiceSite]: string
				[AccrualAdjustmentFields.CurrencyCode]: number
				[AccrualAdjustmentFields.Notes]: string
			}
		}

		namespace ServiceSite {
			interface Basic extends Add.Basic {
				[AccrualAdjustmentFields.ServiceSite]: LabelValue | null
				[AccrualAdjustmentFields.ServiceOccurrenceId]: BasicNumber
				[AccrualAdjustmentFields.BillingAgency]: BasicString
				[AccrualAdjustmentFields.AccountGlRevenue]: BasicString
				[AccrualAdjustmentFields.AccountGlExpense]: BasicString
			}

			interface Valid extends Basic {
				[AccrualAdjustmentFields.ServiceSite]: LabelValue
				[AccrualAdjustmentFields.CurrencyCode]: number
				[AccrualAdjustmentFields.Notes]: string
			}
		}

		namespace Reclasses {
			interface Basic extends Add.Basic {
				[ReclassesAdjustmentFields.currentServiceSite]: LabelValue | null
				[ReclassesAdjustmentFields.newServiceSite]: LabelValue | null
				[ReclassesAdjustmentFields.currentBillingAgency]: BasicString
				[ReclassesAdjustmentFields.newBillingAgency]: BasicString
				[ReclassesAdjustmentFields.currentAccountGLRevenue]: BasicString
				[ReclassesAdjustmentFields.newAccountGLRevenue]: BasicString
				[ReclassesAdjustmentFields.currentAccountGLExpense]: BasicString
				[ReclassesAdjustmentFields.newAccountGLExpense]: BasicString
				[ReclassesAdjustmentFields.currentDepartmentId]: BasicString
				[ReclassesAdjustmentFields.newDepartmentId]: BasicString
				[ReclassesAdjustmentFields.currentRegion]: BasicString
				[ReclassesAdjustmentFields.newRegion]: BasicString
			}

			interface Valid extends Basic {
				[ReclassesAdjustmentFields.currentServiceSite]: LabelValue
				[ReclassesAdjustmentFields.newServiceSite]: LabelValue
				[ReclassesAdjustmentFields.currentDepartmentId]: string
				[ReclassesAdjustmentFields.newDepartmentId]: string
				[ReclassesAdjustmentFields.currentRegion]: string
				[ReclassesAdjustmentFields.newRegion]: string
				[AccrualAdjustmentFields.CurrencyCode]: number
				[AccrualAdjustmentFields.Notes]: string
			}
		}

		namespace Request {
			interface Basic {
				$type: ManualAdjustmentRequestType
				journalDate: string
				revenue: BasicNumber
				expense: BasicNumber
				currencyId: number
				notes: string
			}

			interface ServiceOccurrence extends Basic {
				serviceSiteId: number
				serviceOccurrenceId: number
				accountGlRevenue: BasicString
				accountGlExpense: BasicString
			}

			interface ServiceSite extends Basic {
				serviceSiteId: number
				serviceOccurrenceId: BasicNumber
				accountGlRevenue: BasicString
				accountGlExpense: BasicString
			}

			interface Reclasses extends Basic {
				current: {
					serviceSiteId: number
					billingAgencyId: number
					accountGlRevenue: BasicString
					accountGlExpense: BasicString
					departmentId: string
					region: string
				}
				new: {
					serviceSiteId: number
					billingAgencyId: number
					accountGlRevenue: BasicString
					accountGlExpense: BasicString
					departmentId: string
					region: string
				}
			}
		}
	}

	namespace Search {
		interface Config {
			[AccrualAdjustmentSearchFields.searchByServiceOccurrenceId]: ConfigItem<AccrualAdjustmentSearchFields>
		}
		interface Result {
			[AccrualAdjustmentSearchFields.searchByServiceOccurrenceId]: number[]
		}

		interface ServiceSiteConfig {
			[ServiceSiteSearchFields.searchById]: ConfigItem<ServiceSiteSearchFields>
			[ServiceSiteSearchFields.searchByName]: ConfigItem<ServiceSiteSearchFields>
		}

		interface ServiceSiteResult {
			[ServiceSiteSearchFields.searchById]: Entity[]
			[ServiceSiteSearchFields.searchByName]: Entity[]
		}
	}

	namespace Filter {
		interface Config {
			[AccrualAdjustmentFilterFields.BillingAgencyId]: ConfigFilterItem<string, string>
			[AccrualAdjustmentFilterFields.AdjustmentType]: ConfigFilterItem<string, string>
			[AccrualAdjustmentFilterFields.JournalDateMonth]: ConfigFilterItem<string, string>
			[AccrualAdjustmentFilterFields.Status]: ConfigFilterItem<string, string>
		}
	}
}
